import { castArray, forEach, isArray, isString, mapKeys, sortBy, uniqueId } from 'lodash-es';

import {
  CollectionConfig,
  CollectionItem,
  CollectionItemChangeReason,
  OnCollectionItemChangeCallback,
  RegisterCollectionService,
} from 'types';

import { services } from './register';

const DEFAULT_COLLECTION_ORDER = 1000;
const FILTER_ORDER_DEFAULT = 0;
const FILTER_ORDER_NORMAL = 1;

const convertConfigToItem = (
  configs: CollectionConfig | CollectionConfig[],
  servicePrefix: string,
  defaultOrder: number,
) => {
  const items: CollectionItem[] = castArray(configs).map((config) => {
    const newConfig: CollectionItem = {
      ...config,
      id: config.id ?? uniqueId(`${servicePrefix}_`),
      order: config.order ?? defaultOrder,
    };
    return newConfig;
  });

  return items;
};

type CollectionFilterFn = {
  (collectionItems?: CollectionItem[]): CollectionItem[];
  id: string;
  remove: VoidFunction;
};
const getItems = (filterName: string) => {
  const defaultItems: CollectionItem[] = [];
  const items = services.doFilter(filterName, defaultItems);
  return sortBy(items, 'order');
};

const createFilterFn = (filterName, item: CollectionItem) => {
  const filterFn = (collectionItems: CollectionItem[] = []) => {
    collectionItems.push(item);
    return collectionItems;
  };
  filterFn.id = item.id;
  filterFn.remove = () => {
    services.clearFilter(filterName, filterFn);
  };
  return filterFn;
};

const registerCollection: RegisterCollectionService = (
  servicePrefix: string,
  defaultItems: CollectionConfig[] = [],
  defaultOrder = DEFAULT_COLLECTION_ORDER,
) => {
  if (services[servicePrefix]) {
    throw Error(`Unable to register collection service '${servicePrefix}': service already exists!`);
  }

  const { registerService, registerFilter, getFilterFns, registerAction, executeAction, clearAction } = services;

  const NAME_PREFIX = `AppFrame:Collection:${servicePrefix}`;
  const ACTION_ON_ITEM_CHANGE = `${NAME_PREFIX}:onItemChange`;

  registerService(`${servicePrefix}.addItem`, (itemConfig: CollectionConfig | CollectionConfig[]) => {
    const items = convertConfigToItem(itemConfig, servicePrefix, defaultOrder);
    items.forEach((item) => {
      const filterFn: CollectionFilterFn = createFilterFn(NAME_PREFIX, item);
      registerFilter(NAME_PREFIX, filterFn, { order: FILTER_ORDER_NORMAL });
    });

    services.executeAction(ACTION_ON_ITEM_CHANGE, CollectionItemChangeReason.Add);

    return isArray(itemConfig) ? items : items[0];
  });

  registerService(
    `${servicePrefix}.removeItem`,
    (itemsOrIds: string | string[] | CollectionConfig | CollectionConfig[]) => {
      const idMap = mapKeys(castArray(itemsOrIds).map((itemOrId) => (isString(itemOrId) ? itemOrId : itemOrId.id)));
      const filterFns = getFilterFns(NAME_PREFIX);
      forEach(filterFns, (filterFn) => {
        const fn = filterFn.fn as CollectionFilterFn;
        if (idMap[fn.id]) {
          fn.remove();
        }
      });
      services.executeAction(ACTION_ON_ITEM_CHANGE, CollectionItemChangeReason.Remove);
    },
  );

  registerService(`${servicePrefix}.clear`, () => {
    const filterFns = getFilterFns(NAME_PREFIX);
    forEach(filterFns, (filterFn) => {
      const fn = filterFn.fn as CollectionFilterFn;
      fn.remove();
    });
    services.executeAction(ACTION_ON_ITEM_CHANGE, CollectionItemChangeReason.Remove);
  });

  registerService(`${servicePrefix}.updateItem`, (items: CollectionItem | CollectionItem[]) => {
    const idMap = mapKeys(castArray(items), (item) => item.id);
    const filterFns = getFilterFns(NAME_PREFIX);

    forEach(filterFns, (filterFn, idx) => {
      const fn = filterFn.fn as CollectionFilterFn;
      const item = idMap[fn.id];
      if (item) {
        const newFilterFn = createFilterFn(NAME_PREFIX, item);
        filterFns[idx].fn = newFilterFn;
      }
    });

    services.executeAction(ACTION_ON_ITEM_CHANGE, CollectionItemChangeReason.Update);

    return items;
  });

  registerService(`${servicePrefix}.getItems`, () => getItems(NAME_PREFIX));

  registerService(`${servicePrefix}.getItemAt`, (idx: number) => {
    const items = getItems(NAME_PREFIX);
    return items[idx] || null;
  });

  // default items handling
  const defaultItemsState: {
    filterFns: CollectionFilterFn[];
  } = {
    filterFns: [],
  };

  const setDefaultItems = (itemConfigs: CollectionConfig[]) => {
    defaultItemsState.filterFns.forEach((filterFn) => filterFn.remove());

    const items = convertConfigToItem(itemConfigs, servicePrefix, defaultOrder);
    defaultItemsState.filterFns = items.map((item) => {
      const filterFn: CollectionFilterFn = createFilterFn(NAME_PREFIX, item);
      registerFilter(NAME_PREFIX, filterFn, { order: FILTER_ORDER_DEFAULT });
      return filterFn;
    });

    executeAction<CollectionItemChangeReason>(ACTION_ON_ITEM_CHANGE, CollectionItemChangeReason.Default);
  };

  registerService(`${servicePrefix}.setDefaultItems`, (itemConfigs: CollectionConfig[]) => {
    setDefaultItems(itemConfigs);
  });

  registerService(`${servicePrefix}.onItemChange`, (fn: OnCollectionItemChangeCallback) => {
    registerAction(ACTION_ON_ITEM_CHANGE, fn);
    return () => clearAction(ACTION_ON_ITEM_CHANGE, fn);
  });

  setDefaultItems(defaultItems);
};

const initCollectionServices = () => {
  services.registerService('registerCollection', registerCollection);
};

export default initCollectionServices;
